<template>
  <div class="process-section">
    <h1 v-if="title" class="process-title mb-10 text-center">
      {{ title }}
    </h1>
    <v-timeline 
      :dense="$vuetify.breakpoint.smAndDown"
      class="enhanced-timeline"
    >
      <v-timeline-item 
        v-for="(item, i) in processSteps" 
        :key="i" 
        :color="getStepColor(item.color)" 
        large
        class="timeline-item-animated"
      >
        <template v-slot:icon>
          <v-icon class="timeline-icon white--text" size="26">
            {{ item.icon }}
          </v-icon>
        </template>
        
        <v-card 
          class="timeline-card elevation-3"
        >
          <!-- Color Accent Strip on top of each card -->
          <div class="card-accent-strip" :style="{ background: getStepGradient(item.color) }"></div>

          <!-- Subtle Background Watermark Number -->
          <div class="card-number">{{ i + 1 }}</div>
          
          <v-card-text class="pa-6">
            <!-- Header: Icon Avatar + Title + Phase Chip -->
            <div class="d-flex align-center justify-space-between flex-wrap mb-4 card-header-row">
              <div class="d-flex align-center flex-grow-1">
                <div 
                  class="step-icon-badge" 
                  :style="{ background: getStepLightBg(item.color) }"
                  :class="$vuetify.rtl ? 'ml-3' : 'mr-3'"
                >
                  <v-icon :color="getStepColor(item.color)" size="26">
                    {{ item.icon }}
                  </v-icon>
                </div>
                <h3 class="card-title mb-0">
                  {{ item.title }}
                </h3>
              </div>
              
              <div 
                v-if="item.duration" 
                class="step-chip my-1"
                :style="{ 
                  background: getStepLightBg(item.color), 
                  color: getStepColor(item.color),
                  borderColor: getStepBorderColor(item.color)
                }"
              >
                {{ item.duration }}
              </div>
            </div>
            
            <!-- Description -->
            <p class="card-description mb-0">
              {{ item.dec }}
            </p>
          </v-card-text>
        </v-card>
      </v-timeline-item>
    </v-timeline>
  </div>
</template>

<script>
export default {
  name: 'ProcessTimeline',
  props: {
    title: {
      type: String,
      default: ''
    },
    processSteps: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  methods: {
    getStepColor(colorName) {
      const colors = {
        indigo: '#3F51B5',
        teal: '#00897B',
        purple: '#7C3AED',
        'deep-purple': '#7C3AED',
        red: '#E53935',
        'red darken-1': '#E53935',
        blue: '#1976D2',
        amber: '#F59E0B',
        green: '#10B981'
      };
      return colors[colorName] || colorName || '#3F51B5';
    },
    getStepGradient(colorName) {
      const color = this.getStepColor(colorName);
      return `linear-gradient(90deg, ${color} 0%, ${color}CC 100%)`;
    },
    getStepLightBg(colorName) {
      const color = this.getStepColor(colorName);
      return `${color}14`; // ~8% opacity
    },
    getStepBorderColor(colorName) {
      const color = this.getStepColor(colorName);
      return `${color}33`; // ~20% opacity
    }
  }
};
</script>

<style lang="scss" scoped>
.process-section {
  padding: 20px 0;
  position: relative;
  
  .process-title {
    font-size: 2.2rem;
    font-weight: 800;
    color: #1e293b;
    position: relative;
  }
}

// Enhanced Timeline
.enhanced-timeline {
  padding: 20px 0;
  
  ::v-deep .v-timeline-item__dot {
    box-shadow: 0 4px 14px rgba(63, 81, 181, 0.28);
    transition: all 0.3s ease;
    
    &:hover {
      transform: scale(1.1);
      box-shadow: 0 6px 20px rgba(63, 81, 181, 0.45);
    }
  }
  
  ::v-deep .v-timeline-item__divider {
    min-width: 64px;
  }
  ::v-deep .v-timeline-item__body {
    max-width: calc(50% - 32px) !important;
  }
}

// Timeline Card
.timeline-card {
  position: relative;
  overflow: hidden;
  border-radius: 18px !important;
  background: #ffffff !important;
  border: 1px solid rgba(63, 81, 181, 0.12) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 6px 24px rgba(36, 54, 128, 0.08) !important;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 32px rgba(36, 54, 128, 0.14) !important;
    border-color: rgba(63, 81, 181, 0.25) !important;

    .card-number {
      opacity: 0.07;
      transform: scale(1.05);
    }
  }
}

.card-accent-strip {
  height: 4px;
  width: 100%;
}

// Step Avatar Icon Badge
.step-icon-badge {
  width: 46px;
  height: 46px;
  min-width: 46px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
}

// Phase Chip
.step-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 14px;
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 700;
  border: 1px solid transparent;
  white-space: nowrap;
}

// Card Number Background
.card-number {
  position: absolute;
  top: -15px;
  font-size: 110px;
  font-weight: 900;
  color: #1e293b;
  opacity: 0.035;
  line-height: 1;
  transition: all 0.3s ease;
  user-select: none;
  pointer-events: none;
  z-index: 0;
  
  [dir="ltr"] &, &:not([dir="rtl"]) {
    right: 16px;
  }
  
  [dir="rtl"] & {
    left: 16px;
  }
}

// Card Title
.card-title {
  font-size: 1.25rem;
  font-weight: 800;
  line-height: 1.35;
  color: #1e293b;
  position: relative;
  z-index: 1;
}

// Card Description
.card-description {
  font-size: 0.98rem;
  line-height: 1.7;
  color: #475569;
  position: relative;
  z-index: 1;
}

.card-header-row {
  position: relative;
  z-index: 1;
  gap: 12px;
}

@media (max-width: 600px) {
  .card-title {
    font-size: 1.1rem;
  }
  
  .card-description {
    font-size: 0.9rem;
  }
  
  .enhanced-timeline ::v-deep .v-timeline-item__body {
    max-width: 100% !important;
  }
}
</style>
